import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import SectionBadge from "@/components/site/SectionBadge";
import { track } from "@/lib/analytics";

const PRACTICES = [
  {
    slug: "strategy-commercial-transformation",
    name: "Strategy & Commercial Transformation",
    desc: "Decide where to grow — and build the commercial system required to deliver it.",
    capabilities:
      "Growth Strategy · Product Portfolio · Pricing · Go-To-Market · Route-to-Market · Sales · Commercial Excellence",
    image: "/photos/expertise-strategy.jpg",
    imageAlt: "Indian strategy team reviewing growth plans on a glass planning wall",
  },
  {
    slug: "market-customer-intelligence",
    name: "Market & Customer Intelligence",
    desc: "Replace assumptions with evidence — and evidence with better commercial decisions.",
    capabilities:
      "Markets · Customers · Segmentation · Competition · Decision Journeys · Foresight",
    image: "/photos/band-intelligence.jpg",
    imageAlt: "Indian market researcher interviewing a shopper with a tablet in a bright retail aisle",
  },
  {
    slug: "brand-marketing-communications",
    name: "Brand, Marketing & Communications",
    desc: "Build brands customers prefer — and marketing that turns preference into growth.",
    capabilities:
      "Brand Strategy · Architecture · Identity · Marketing · Creative · Reputation",
    image: "/photos/expertise-marketing.jpg",
    imageAlt: "Indian team reviewing a colorful market analytics dashboard on a large screen",
  },
  {
    slug: "digital-commerce",
    name: "Digital & Commerce",
    desc: "Acquire, convert, transact and retain customers profitably across digital channels.",
    capabilities:
      "Performance Marketing · Marketplaces · D2C · CRO · CRM · SEO · Social",
    image: "/photos/expertise-execution.jpg",
    imageAlt: "Indian team working at laptops around a long table in a bright office",
  },
];

export default function Expertise() {
  return (
    <section
      id="expertise"
      data-testid="expertise-section"
      className="pt-16 md:pt-24 pb-20 md:pb-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <SectionBadge label="Our Expertise" />
        <div className="mt-6 grid grid-cols-12 gap-6 md:gap-10 mb-14 md:mb-16">
          <div className="col-span-12 md:col-span-6">
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Four practices. <br />
              One growth system.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:flex md:flex-col md:justify-end">
            <p className="text-lg text-zinc-600 leading-relaxed max-w-lg">
              Growth problems rarely sit within one function. BLEZZINGS
              connects strategy, intelligence, brand, marketing, sales, digital
              and commerce so every part of the commercial system works toward
              the same outcome.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 reveal stagger-reveal">
          {PRACTICES.map((p, i) => (
            <Link
              key={p.slug}
              to={`/capabilities/${p.slug}`}
              data-testid={`expertise-card-${p.slug}`}
              onClick={() => track("practice_card_open", { practice: p.slug })}
              className="group relative flex flex-col bg-white border border-zinc-200 overflow-hidden transition-all duration-500 hover:border-black hover:-translate-y-1"
            >
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-zinc-100">
                <img
                  src={p.image}
                  alt={p.imageAlt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover img-edit transition-transform duration-700 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-black/5" />
              </div>
              <div className="flex flex-col flex-1 p-8 md:p-12">
              <div className="flex items-baseline justify-between gap-4">
                <span className="overline">{String(i + 1).padStart(2, "0")}</span>
                <ArrowUpRight className="h-5 w-5 text-zinc-300 transition-all duration-300 group-hover:text-black group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
              <h3 className="font-serif-display text-3xl md:text-4xl mt-6 leading-tight text-black">
                {p.name}
              </h3>
              <p className="mt-4 text-base text-zinc-600 leading-relaxed max-w-md">
                {p.desc}
              </p>
              <p className="mt-8 text-[12px] font-medium tracking-[0.14em] uppercase text-zinc-500 leading-relaxed">
                {p.capabilities}
              </p>
              <div className="mt-8 pt-6 border-t border-black/10 inline-flex items-center gap-2 text-sm font-medium text-black">
                Explore {p.name}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
              </div>
              <span
                aria-hidden
                className="absolute left-0 bottom-0 h-[3px] w-0 bg-black transition-all duration-500 group-hover:w-full"
              />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
